/** The same 500ms the field messages unfold over, so one screen moves at one speed. */
const DURATION_MS = 500;

/**
 * jQuery's `swing`, which is `cubic-bezier(0.42, 0, 0.58, 1)` — ease-in-out.
 *
 * <p>The curve matters more than the number. An expo-out covers ninety per cent of the
 * distance in the first hundred and fifty milliseconds and creeps through the rest, so the
 * movement is effectively over before the eye arrives; `form-feedback.css` records the same
 * finding for the message animation, and this is the same motion on a larger object.
 */
function ease(t: number) {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

/**
 * Scrolls an element to the middle of the window, over half a second.
 *
 * <h2>Why not `scrollIntoView({ behavior: 'smooth' })`</h2>
 *
 * **It has no duration.** The browser decides, and Chrome's is fast enough that a jump to
 * the top of a long form reads as a teleport rather than a movement — which is how it was
 * reported. There is no option to pass, so matching the rest of the screen's motion means
 * animating it here.
 *
 * <h2>Two cases fall back to the native call, and both are deliberate</h2>
 *
 * <ul>
 * <li><b>`prefers-reduced-motion`</b> — somebody who has asked for less movement gets the
 *     jump. The position still changes, because the alternative is a message they never
 *     reach.</li>
 * <li><b>No layout</b> — jsdom reports every rectangle as zero, so the arithmetic below
 *     would scroll to the top of the document and call it a hit. Falling back keeps a test
 *     able to assert which element was aimed at, which is the part worth asserting.</li>
 * </ul>
 */
/**
 * Whether the reader has asked for less movement.
 *
 * <p>Guarded: jsdom does not implement `matchMedia`, and an unguarded call throws inside the
 * callback that was about to scroll — so nothing moved and nothing said why.
 */
function prefersLessMotion() {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Animates the window to an absolute offset, over the same half second as everything else.
 *
 * <p>The primitive the two exported scrolls are built from, so there is one easing curve and
 * one duration in the application rather than a second copy that drifts.
 *
 * <p>A reader who has asked for less motion is moved instantly instead. The position still
 * changes, because not moving at all leaves somebody where they did not want to be.
 *
 * @param to the offset from the top of the document, clamped by the browser
 */
export function smoothScrollTo(to: number) {
  const from = window.scrollY;

  if (prefersLessMotion()) {
    window.scrollTo(0, to);
    return;
  }

  const started = performance.now();

  function step(now: number) {
    const progress = Math.min(1, (now - started) / DURATION_MS);
    window.scrollTo(0, from + (to - from) * ease(progress));

    if (progress < 1) {
      window.requestAnimationFrame(step);
    }
  }

  window.requestAnimationFrame(step);
}

export function smoothScrollIntoView(target: Element) {
  const box = target.getBoundingClientRect();
  const noLayout = box.height === 0 && box.top === 0;

  if (prefersLessMotion() || noLayout) {
    target.scrollIntoView({ block: 'center' });
    return;
  }

  smoothScrollTo(window.scrollY + box.top - (window.innerHeight - box.height) / 2);
}
